'use client';

import React, { useCallback, useEffect, useId } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export type DrawerSide = 'right' | 'left' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  side?: DrawerSide;
  size?: DrawerSize;
  title?: string;
  subtitle?: string;
  /** Conteúdo extra no cabeçalho, abaixo do título/subtítulo (ex.: um badge de
   *  status). Mantém o corpo do drawer enxuto. */
  headerAccessory?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  hideCloseButton?: boolean;
  className?: string;
  /** Aria label do botao de fechar - traduzir conforme contexto. */
  closeLabel?: string;
}

const SIDE_CLASS: Record<DrawerSide, string> = {
  right: '',
  left: 'ds-drawer--left',
  bottom: 'ds-drawer--bottom',
};

const SIZE_CLASS: Record<DrawerSize, string> = {
  sm: 'ds-drawer--sm',
  md: '',
  lg: 'ds-drawer--lg',
};

/**
 * Wrapper React do padrao `.ds-drawer` do design system. Suporta as 3
 * variantes (right/left/bottom), close on ESC, lock de scroll do body
 * e click-outside.
 */
export function Drawer({
  isOpen,
  onClose,
  side = 'right',
  size = 'md',
  title,
  subtitle,
  headerAccessory,
  children,
  footer,
  hideCloseButton = false,
  className,
  closeLabel = 'Fechar',
}: DrawerProps) {
  const generatedId = useId();
  const titleId = title ? `drawer-title-${generatedId.replace(/:/g, '')}` : undefined;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = prevOverflow;
      };
    }
    return undefined;
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const showHeader = title || subtitle || headerAccessory || !hideCloseButton;

  return (
    <div
      className="ds-drawer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        className={cn('ds-drawer', SIDE_CLASS[side], SIZE_CLASS[size], className)}
      >
        {showHeader && (
          <header className="ds-drawer__head">
            <div>
              {title && (
                <h3 id={titleId} className="ds-drawer__title">
                  {title}
                </h3>
              )}
              {subtitle && <p className="ds-drawer__subtitle">{subtitle}</p>}
            </div>
            {(headerAccessory || !hideCloseButton) && (
              <div className="ds-drawer__head-actions">
                {headerAccessory}
                {!hideCloseButton && (
                  <button
                    type="button"
                    className="ds-drawer__close"
                    onClick={onClose}
                    aria-label={closeLabel}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
          </header>
        )}
        <div className="ds-drawer__body">{children}</div>
        {footer && <footer className="ds-drawer__foot">{footer}</footer>}
      </aside>
    </div>
  );
}
